/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <ListOfJobs {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section } from "@bw/bits"
import { ListFilters } from "@bw/partials"
import { ListOfJobs as ListOfJobsModule } from "@bw/modules"
import useCollectionJobs from "hooks/useCollectionJobs"

const ListOfJobs = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  backgroundVariant,
  isFollowedByModuleWithBackgroundVariant,
  jobType,
  maxItems,
  orderedBy,
  displayFilters,
}) => {
  const jobs = useCollectionJobs({
    type: jobType?.slug,
    limit: maxItems,
    order: orderedBy?.value,
  })

  if (jobs.length === 0) {
    return null
  }

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        idAttribute,
        classAttribute,
        backgroundVariant,
        isFollowedByModuleWithBackgroundVariant,
      }}
    >
      {displayFilters ? (
        <ListFilters items={jobs}>
          {({ items }) => <ListOfJobsModule jobs={items} />}
        </ListFilters>
      ) : (
        <ListOfJobsModule jobs={jobs} />
      )}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on ListOfJobsModule {
 *   ...ListOfJobsModuleFragment
 * }
 *
 * This is also what you will get in the props of your ListOfJobs
 * resolver component.
 */
export const query = graphql`
  fragment ListOfJobsModuleFragment on ListOfJobsModule {
    displayFilters
    maxItems
    orderedBy {
      value
    }
    jobType {
      slug
    }
  }
`

export { ListOfJobs }
